<template>
  <b-container>
    <b-row class="headline" align-v="center">
      <b-col cols="*">
        <img class="avatar" :src="artistAvatar">
      </b-col>
      <b-col class="col-7">
        <h1>{{ artistName }}</h1>
        <router-link class="followers" :to="{name: 'artist-followers'}">{{ followerCount }}</router-link>
      </b-col>
      <b-col class="actions">
        <router-link :to="{name: 'artist-about', params: {artistId: artist.id}}"><i class="sl-icon-eye"></i></router-link>
        <router-link :to="{name: 'artist-profile-edit'}" class="u">Edit information</router-link>
      </b-col>
    </b-row>

    <b-row v-if="!hasPayoutMethod">
      <b-col class="no-payout-info">
        <p>
          Payouts are not set up.
        </p>
        <p>
          Please make sure that you have successfully connected with Stripe, in order to enable
          payment processing and allow fans to access your content.
        </p>
        <router-link :to="{name: 'artist-onboarding'}">Connect with Stripe</router-link>
      </b-col>
    </b-row>

    <b-row class="summaries" v-if="hasPayoutMethod && stats">
      <b-col class="col-12 col-md-4 pb-3">
        <div class="donations">
          <div class="title"><font-awesome-icon icon="plus" /> donations</div>
          <div class="stats">
            <div class="count">{{ stats.donation.count }}</div>
            <div class="revenue">{{ stats.donation.total.toFixed(2) }} <span class="currency">US$</span></div>
          </div>
        </div>
      </b-col>
      <b-col class="col-12 col-md-4 pb-3">
        <div class="downloads">
          <div class="title"><i class="sl-icon-cloud-download"></i> downloads</div>
          <div class="stats">
            <div class="count">{{ stats.purchase.count }}</div>
            <div class="revenue">{{ stats.purchase.total.toFixed(2) }} <span class="currency">US$</span></div>
          </div>
        </div>
      </b-col>
      <b-col class="col-12 col-md-4 pb-1">
        <div class="livestreams">
          <div class="title"><i class="sl-icon-camrecorder"></i> live streams</div>
          <div class="stats">
            <div class="count">{{ stats.booking.count }}</div>
            <div class="revenue">{{ stats.booking.total.toFixed(2) }} <span class="currency">US$</span></div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="hasPayoutMethod && transactions">
      <b-col>
        <div class="recent">
          <div class="heading">
            <div class="title">Recent Transactions</div>
            <div class="stripe">
              <a href="https://dashboard.stripe.com/" target="_blank">Stripe dashboard</a>
            </div>
          </div>
          <table class="table-responsive-md">
            <thead>
              <tr>
                <th class="left">User</th>
                <th>Type</th>
                <th>Title</th>
                <th>Amount</th>
                <th>Date</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in transactions" :key="t.id">
                <td class="left" v-if="t.fan.is_active">
                  <router-link :to="{name: 'fan-profile', params: {id: t.fan.id}}">{{ displayName(t.fan)}}</router-link>
                </td>
                <td class="left" v-else>{{ displayName(t.fan)}}</td>
                <td>{{ getType(t) }}</td>
                <td class="left">{{ t.description }}</td>
                <td>${{ t.amount.toFixed(2) }}</td>
                <td>{{ formatDate(t) }}</td>
                <td><router-link :to="{name: 'artist-my-activity', query: { thanks: t.fan.username }}" class="thanks-link" v-if="t.fan.is_active">Say thanks</router-link></td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-col>
    </b-row>

  </b-container>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  .headline {
     h1 {
      @include media-breakpoint-down(md) {
        font-size: 1.5rem;
      }
    }

    .actions {
      text-align: right;
      @include media-breakpoint-down(md) {
        margin-bottom: 1em;
      }

      i {
        margin-right: 1em;
      }

      a {
        color: $white;

        &.u {
          text-decoration: underline;
        }
      }
    }
  }

  .avatar {
    width: 8em;
    height: 8em;
    border-radius: 100%;
    margin: 2em;
    @include media-breakpoint-down(md) {
      width: 5em;
      height: 5em;
      margin-right: 1em;
      margin-bottom: 1.2em;
    }
  }

  h1 {
    margin-bottom: 0.25em;
  }

  .followers {
    color: $white;
  }

  .no-payout-info {
    text-align: center;
    padding: 0.5em;
    margin: 0 0.5em 1em 0.5em;
    background-image: linear-gradient(93deg, $darkish-pink, $blue-purple);

    a {
      color: $white;
      text-decoration: underline;
    }
  }

  .summaries {
    .donations {
      padding: 10px;
      border-radius: 4px;
      background-image: linear-gradient(109deg, $darkish-pink, $blue-purple);
      box-shadow: 4px 4px 9px 0 rgba(0, 0, 0, 0.21);

      .count {
        border-right: 1px solid #d27dc1;
      }
    }

    .downloads {
      padding: 10px;
      border-radius: 4px;
      background-image: linear-gradient(109deg, #43c2d0, #41b2ba);
      box-shadow: 4px 4px 9px 0 rgba(0, 0, 0, 0.21);

      .count {
        border-right: 1px solid #86d3da;
      }
    }

    .livestreams {
      padding: 10px;
      border-radius: 4px;
      background-color: $slate-blue;
      box-shadow: 4px 4px 9px 0 rgba(0, 0, 0, 0.21);

      .count {
        border-right: 1px solid #9a9abe;
      }
    }

    .title {
      font-weight: 900;
      font-size: 0.8em;
    }

    .stats {
      display: flex;
      font-size: 1.5em;
      font-weight: 100;

      .count {
        flex: 1;
      }

      .revenue {
        flex: 1.5;
        text-align: right;

        .currency {
          font-size: 0.6em;
        }
      }
    }
  }

  .recent {
    margin-top: 1em;
    font-size: 0.8em;
    background: $slate-blue;
    border-radius: 4px;

    .heading {
      display: flex;
      align-items: center;

      .title {
        padding: 0.5em;
        font-size: 1.5em;
        font-weight: bold;
        flex-grow: 1;
      }

      .stripe {
        text-align: right;
        padding: 0.5em;

        a {
          color: $white;
          text-decoration: underline;

          &:hover {
            text-decoration: none;
          }
        }
      }
    }

    table {
      width: 100%;

      th {
        padding: 1em;
        font-weight: normal;
        text-align: center;

        &.left {
          text-align: left;
        }
      }

      tbody {
        tr {
          border-top: 1px solid rgba(26, 26, 44, 0.39);

          td {
            padding: 1em;
            text-align: center;

            &.left {
              text-align: left;
            }

            a {
              color: #fc65a9;
            }
          }
        }

        .thanks-link {
          color: $white;
          text-decoration: underline;
        }
      }
    }
  }

</style>

<script>
import { mapGetters } from 'vuex'
import moment from '@/utils/moment'

import { profileHelpers } from '@/store/helpers'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

export default {
  name: 'Dashboard',
  components: {
    FontAwesomeIcon
  },
  data () {
    return {
      stats: null,
      start_date: null,
      before_date: null,
      transactions: null
    }
  },
  computed: {
    followerCount () {
      const count = this.artist.follower_count || 0
      return `${count} Follower` + ((count === 1) ? '' : 's')
    },
    ...mapGetters('artist', ['artist', 'artistName', 'artistAvatar', 'hasPayoutMethod'])
  },
  created () {
    this.initPage()
  },
  methods: {
    async initPage () {
      // We might have just returned from the Stripe connect dance and the UI doesn't know the artist
      // is connected, so let's refresh.
      if (!this.hasPayoutMethod) {
        await this.$store.dispatch('artist/getCurrentArtist')
      }

      const response = await this.$store.dispatch('payment/listArtistTransactions')
      this.transactions = await response.all()

      let stats = {
        donation: {count: 0, total: 0},
        purchase: {count: 0, total: 0},
        booking: {count: 0, total: 0}
      }

      for (let t of this.transactions) {
        stats[t.type].count++
        stats[t.type].total += t.amount
      }

      this.stats = stats
    },
    formatDate (t) {
      return moment(t.created_at).format('MM/DD/YYYY h:mm a')
    },
    getType (t) {
      return (t.type === 'booking') ? 'live stream' : t.type
    },
    ...profileHelpers
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/artist/Dashboard.vue